import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  UserCheck,
  Eye,
  Search,
  Loader2,
  Shield,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { cn } from '@/lib/utils';

const emptyForm = () => ({ name: '', manager_ids: [], staff_ids: [] });

function Avatar({ name, className }) {
  return (
    <div
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white',
        className
      )}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function SelectedChips({ people, ids, onRemove, tone = 'indigo' }) {
  const selected = people.filter((p) => ids.includes(p.staff_id));
  if (!selected.length) return null;
  const chip =
    tone === 'purple'
      ? 'bg-purple-50 text-purple-800 ring-purple-100'
      : 'bg-indigo-50 text-indigo-800 ring-indigo-100';
  return (
    <div className="flex flex-wrap gap-1.5">
      {selected.map((p) => (
        <span
          key={p.staff_id}
          className={cn(
            'inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
            chip
          )}
        >
          <span className="truncate">{p.name}</span>
          <button
            type="button"
            onClick={() => onRemove(p.staff_id)}
            className="rounded-full p-0.5 hover:bg-black/5"
            aria-label={`Remove ${p.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

function PeopleChecklist({
  people,
  selectedIds,
  onToggle,
  search,
  onSearchChange,
  emptyLabel,
  disabled,
  testIdPrefix,
  tone = 'indigo',
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return people;
    return people.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.department?.toLowerCase().includes(q)
    );
  }, [people, search]);

  const selected = tone === 'purple' ? 'bg-purple-50 ring-1 ring-purple-200' : 'bg-indigo-50 ring-1 ring-indigo-200';
  const box = tone === 'purple' ? 'border-purple-600 bg-purple-600' : 'border-indigo-600 bg-indigo-600';
  const avatar =
    tone === 'purple'
      ? 'bg-gradient-to-br from-purple-500 to-pink-600'
      : 'bg-gradient-to-br from-indigo-500 to-purple-600';

  if (!people.length) {
    return (
      <p className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-500 ring-1 ring-slate-100">
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="relative border-b border-slate-100 bg-slate-50/80 px-3 py-2.5">
        <Search className="absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search name or email…"
          disabled={disabled}
          className="h-9 rounded-xl border-slate-200 bg-white pl-9 text-sm shadow-none"
        />
      </div>
      <div className="max-h-56 space-y-1 overflow-y-auto p-2">
        {filtered.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-slate-400">No matches</p>
        ) : (
          filtered.map((person) => {
            const on = selectedIds.includes(person.staff_id);
            return (
              <button
                key={person.staff_id}
                type="button"
                onClick={() => onToggle(person.staff_id)}
                disabled={disabled}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition',
                  on ? selected : 'hover:bg-slate-50'
                )}
                data-testid={`${testIdPrefix}-${person.staff_id}`}
              >
                <div
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                    on ? box : 'border-slate-300 bg-white'
                  )}
                >
                  {on && (
                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <Avatar name={person.name} className={avatar} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{person.name}</p>
                  <p className="truncate text-xs text-slate-500">
                    {person.email}
                    {person.department ? ` · ${person.department}` : ''}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

const TeamsPage = () => {
  const { t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [managers, setManagers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [pickerTab, setPickerTab] = useState('members'); // members | managers
  const [peopleSearch, setPeopleSearch] = useState('');

  useEffect(() => {
    fetchTeams();
    fetchManagers();
    fetchAssignableStaff();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/admin/teams`, { withCredentials: true });
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/admin/managers`, { withCredentials: true });
      setManagers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  };

  const fetchAssignableStaff = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, { withCredentials: true });
      const list = (response.data || []).filter(
        (s) => s.staff_type !== 'manager' && s.status === 'active'
      );
      setStaffList(list);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const openCreate = () => {
    setFormData(emptyForm());
    setSelectedTeam(null);
    setPickerTab('members');
    setPeopleSearch('');
    setShowAddDialog(true);
  };

  const openEdit = async (team) => {
    setSelectedTeam(team);
    setLoadingEdit(true);
    setPickerTab('members');
    setPeopleSearch('');
    setShowEditDialog(true);
    try {
      const response = await axios.get(`${API}/admin/teams/${team.team_id}`, {
        withCredentials: true,
      });
      const detail = response.data;
      const managerPool = managers.length ? managers : detail.managers || [];
      setFormData({
        name: detail.name || team.name,
        manager_ids: (detail.manager_ids || []).filter((id) =>
          managerPool.some((m) => m.staff_id === id)
        ),
        staff_ids: detail.staff_ids || (detail.staff_members || []).map((s) => s.staff_id),
      });
      setSelectedTeam(detail);
    } catch (error) {
      setFormData({
        name: team.name,
        manager_ids: (team.manager_ids || []).filter((id) =>
          managers.some((m) => m.staff_id === id)
        ),
        staff_ids: [],
      });
      toast.error('Could not load full team details');
    } finally {
      setLoadingEdit(false);
    }
  };

  const openView = async (team) => {
    try {
      const response = await axios.get(`${API}/admin/teams/${team.team_id}`, {
        withCredentials: true,
      });
      setSelectedTeam(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to fetch team details');
    }
  };

  const handleDelete = async (team) => {
    if (
      !window.confirm(
        `Delete team "${team.name}"? Staff will be removed from this team.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/teams/${team.team_id}`, { withCredentials: true });
      toast.success('Team deleted');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const name = formData.name.trim();
    if (!name) {
      toast.error('Team name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        manager_ids: formData.manager_ids,
        staff_ids: formData.staff_ids,
      };
      if (showEditDialog && selectedTeam) {
        await axios.put(`${API}/admin/teams/${selectedTeam.team_id}`, payload, {
          withCredentials: true,
        });
        toast.success('Team updated');
      } else {
        await axios.post(`${API}/admin/teams`, payload, { withCredentials: true });
        toast.success('Team created');
      }
      setShowAddDialog(false);
      setShowEditDialog(false);
      fetchTeams();
      fetchAssignableStaff();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const toggleManager = (id) => {
    setFormData((prev) => ({
      ...prev,
      manager_ids: prev.manager_ids.includes(id)
        ? prev.manager_ids.filter((x) => x !== id)
        : [...prev.manager_ids, id],
    }));
  };

  const toggleStaff = (id) => {
    setFormData((prev) => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(id)
        ? prev.staff_ids.filter((x) => x !== id)
        : [...prev.staff_ids, id],
    }));
  };

  const filteredTeams = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(
      (team) =>
        team.name?.toLowerCase().includes(q) ||
        team.managers?.some((m) => m.name?.toLowerCase().includes(q))
    );
  }, [teams, searchTerm]);

  const totalStaff = teams.reduce((sum, t) => sum + (t.staff_count || 0), 0);
  const withManagers = teams.filter((t) => (t.managers?.length || 0) > 0).length;
  const formOpen = showAddDialog || showEditDialog;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <div className="relative pb-8" data-testid="teams-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        {/* Hero */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{t('nav.teams')}</h1>
                <p className="mt-1 text-sm text-indigo-50/90">
                  Organize staff into teams and assign managers
                </p>
              </div>
              <Button
                onClick={openCreate}
                data-testid="add-team-btn"
                className="h-10 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 sm:h-11 sm:rounded-2xl sm:px-5"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add Team
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <p className="text-sm font-medium text-slate-600">
              {teams.length} team{teams.length === 1 ? '' : 's'}
            </p>
            <div className="relative w-full max-w-xs sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search teams…"
                className="h-10 rounded-2xl border-slate-200 bg-white pl-9 shadow-sm"
              />
            </div>
          </div>
        </section>

        {/* KPIs */}
        <section className="att-fade-up grid grid-cols-3 gap-2.5 sm:gap-4" style={{ animationDelay: '60ms' }}>
          {[
            { title: 'Teams', value: teams.length, icon: Users, soft: 'bg-purple-50 text-purple-600' },
            { title: 'Staff', value: totalStaff, icon: UserCheck, soft: 'bg-indigo-50 text-indigo-600' },
            { title: 'Managed', value: withManagers, icon: Shield, soft: 'bg-sky-50 text-sky-600' },
          ].map((k) => (
            <div
              key={k.title}
              className="rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm sm:p-4"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl sm:h-10 sm:w-10', k.soft)}>
                  <k.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold tabular-nums text-slate-900 sm:text-2xl">{k.value}</p>
                  <p className="truncate text-[11px] font-medium text-slate-500 sm:text-xs">{k.title}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* List */}
        <section className="att-fade-up" style={{ animationDelay: '120ms' }}>
          {filteredTeams.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-16 text-center">
              <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <h3 className="text-lg font-bold text-slate-900">
                {searchTerm ? 'No matching teams' : 'No teams yet'}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {searchTerm ? `Nothing matches “${searchTerm}”` : 'Create a team and assign people'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={openCreate}
                  className="mt-5 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                  data-testid="create-first-team-btn"
                >
                  Create Team
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredTeams.map((team, idx) => {
                const mgrs = team.managers || [];
                const count = team.staff_count || 0;
                return (
                  <article
                    key={team.team_id}
                    data-testid={`team-card-${team.team_id}`}
                    className="att-fade-up group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:rounded-[1.5rem]"
                    style={{ animationDelay: `${Math.min(idx, 8) * 35 + 120}ms` }}
                  >
                    <button
                      type="button"
                      onClick={() => openView(team)}
                      className="w-full p-4 text-left outline-none transition hover:bg-slate-50/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400/40 sm:p-5"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-base font-bold text-white shadow-md shadow-indigo-500/20">
                          {(team.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="truncate text-base font-bold text-slate-900 sm:text-lg">
                              {team.name}
                            </h3>
                            <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-bold tabular-nums text-indigo-700 ring-1 ring-indigo-100">
                              {count}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                            {count} member{count === 1 ? '' : 's'}
                            {mgrs.length ? ` · ${mgrs.length} manager${mgrs.length === 1 ? '' : 's'}` : ''}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
                        {mgrs.length ? (
                          mgrs.slice(0, 3).map((m) => (
                            <span
                              key={m.staff_id}
                              className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-700 ring-1 ring-purple-100"
                            >
                              <Shield className="h-3 w-3" />
                              {m.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">No manager</span>
                        )}
                        {mgrs.length > 3 && (
                          <span className="text-[11px] font-medium text-slate-400">
                            +{mgrs.length - 3}
                          </span>
                        )}
                      </div>
                    </button>

                    <div className="flex items-center gap-1 border-t border-slate-100 px-2 py-2 sm:px-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openView(team)}
                        className="h-9 flex-1 rounded-xl text-slate-600"
                        data-testid={`view-team-${team.team_id}`}
                      >
                        <Eye className="mr-1 h-3.5 w-3.5" />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(team)}
                        className="h-9 flex-1 rounded-xl text-slate-600"
                        data-testid={`edit-team-${team.team_id}`}
                      >
                        <Edit2 className="mr-1 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(team)}
                        className="h-9 w-9 rounded-xl text-rose-600 hover:bg-rose-50"
                        data-testid={`delete-team-${team.team_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Create / Edit modal */}
      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          if (saving) return;
          if (!open) {
            setShowAddDialog(false);
            setShowEditDialog(false);
          }
        }}
      >
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-1.25rem)] flex-col gap-0 overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:max-w-[480px]">
          <div className="shrink-0 border-b border-slate-100 px-5 pb-4 pt-5 sm:px-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-tight text-slate-900">
                {showEditDialog ? 'Edit team' : 'New team'}
              </DialogTitle>
            </DialogHeader>
            <p className="mt-1 text-sm text-slate-500">
              Name the team, then pick members and managers
            </p>
          </div>

          {loadingEdit ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
                <div className="space-y-1.5">
                  <Label htmlFor="team-name">Team name</Label>
                  <Input
                    id="team-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Sales, Support…"
                    required
                    disabled={saving}
                    className="h-11 rounded-2xl"
                    data-testid="team-name-input"
                    autoFocus
                  />
                </div>

                {/* Selected summary chips */}
                {(formData.staff_ids.length > 0 || formData.manager_ids.length > 0) && (
                  <div className="space-y-2 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100">
                    {formData.staff_ids.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Members · {formData.staff_ids.length}
                        </p>
                        <SelectedChips
                          people={staffList}
                          ids={formData.staff_ids}
                          onRemove={toggleStaff}
                          tone="indigo"
                        />
                      </div>
                    )}
                    {formData.manager_ids.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          Managers · {formData.manager_ids.length}
                        </p>
                        <SelectedChips
                          people={managers}
                          ids={formData.manager_ids}
                          onRemove={toggleManager}
                          tone="purple"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Single picker with tabs */}
                <div className="space-y-2.5">
                  <div className="flex rounded-2xl bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setPickerTab('members');
                        setPeopleSearch('');
                      }}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition',
                        pickerTab === 'members'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      )}
                    >
                      <Users className="h-3.5 w-3.5" />
                      Members
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                          pickerTab === 'members'
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-slate-200/80 text-slate-500'
                        )}
                      >
                        {formData.staff_ids.length}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPickerTab('managers');
                        setPeopleSearch('');
                      }}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition',
                        pickerTab === 'managers'
                          ? 'bg-white text-purple-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      )}
                    >
                      <Shield className="h-3.5 w-3.5" />
                      Managers
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                          pickerTab === 'managers'
                            ? 'bg-purple-50 text-purple-700'
                            : 'bg-slate-200/80 text-slate-500'
                        )}
                      >
                        {formData.manager_ids.length}
                      </span>
                    </button>
                  </div>

                  {pickerTab === 'members' ? (
                    <PeopleChecklist
                      people={staffList}
                      selectedIds={formData.staff_ids}
                      onToggle={toggleStaff}
                      search={peopleSearch}
                      onSearchChange={setPeopleSearch}
                      emptyLabel="No active staff to assign. Add staff first."
                      disabled={saving}
                      testIdPrefix="staff-option"
                      tone="indigo"
                    />
                  ) : (
                    <PeopleChecklist
                      people={managers}
                      selectedIds={formData.manager_ids}
                      onToggle={toggleManager}
                      search={peopleSearch}
                      onSearchChange={setPeopleSearch}
                      emptyLabel='No managers yet. Set a staff type to “Manager” first.'
                      disabled={saving}
                      testIdPrefix="manager-option"
                      tone="purple"
                    />
                  )}
                </div>
              </div>

              <DialogFooter className="shrink-0 gap-2 border-t border-slate-100 bg-white px-5 py-3.5 sm:space-x-0 sm:px-6">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 flex-1 rounded-xl sm:flex-none sm:px-5"
                  disabled={saving}
                  onClick={() => {
                    setShowAddDialog(false);
                    setShowEditDialog(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !formData.name.trim()}
                  className="h-10 flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700 sm:flex-none sm:min-w-[140px]"
                  data-testid="save-team-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : showEditDialog ? (
                    'Save changes'
                  ) : (
                    'Create team'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:max-w-[520px]">
          <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-sm font-bold text-white">
                  {(selectedTeam?.name || '?').charAt(0).toUpperCase()}
                </div>
                {selectedTeam?.name}
              </DialogTitle>
            </DialogHeader>
          </div>
          {selectedTeam && (
            <div className="space-y-5 px-5 py-4 sm:px-6">
              <div>
                <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Managers ({selectedTeam.managers?.length || 0})
                </h4>
                {selectedTeam.managers?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTeam.managers.map((manager) => (
                      <span
                        key={manager.staff_id}
                        className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-100"
                      >
                        <Shield className="h-3 w-3" />
                        {manager.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No managers</p>
                )}
              </div>
              <div>
                <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Members ({selectedTeam.staff_members?.length || 0})
                </h4>
                {selectedTeam.staff_members?.length ? (
                  <div className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-2xl border border-slate-200">
                    {selectedTeam.staff_members.map((staff) => (
                      <div key={staff.staff_id} className="flex items-center gap-3 p-3">
                        <Avatar
                          name={staff.name}
                          className="bg-gradient-to-br from-indigo-500 to-purple-600"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{staff.name}</p>
                          <p className="truncate text-xs text-slate-500">{staff.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-500 ring-1 ring-slate-100">
                    No members yet — edit this team to assign people.
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-2 border-t border-slate-100 px-5 py-3.5 sm:px-6">
            <Button
              variant="outline"
              className="h-10 flex-1 rounded-xl"
              onClick={() => setShowViewDialog(false)}
            >
              {t('common.close')}
            </Button>
            {selectedTeam && (
              <Button
                className="h-10 flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  setShowViewDialog(false);
                  openEdit(selectedTeam);
                }}
              >
                <Edit2 className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
